import { SearchResult } from '@/hooks/useSearch';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Loader2, Search, ArrowRight } from 'lucide-react';

interface SearchDropdownProps {
  results: SearchResult[];
  isSearching: boolean;
  query: string;
  onResultClick: (result: SearchResult) => void;
  onViewAllClick: () => void;
}

const getTypeLabel = (type: SearchResult['type']) => {
  switch (type) {
    case 'service': return 'Serviço';
    case 'product': return 'Produto';
    case 'professional': return 'Profissional';
    case 'page': return 'Página';
    default: return '';
  }
};

const getTypeColor = (type: SearchResult['type']) => {
  switch (type) {
    case 'service': return 'bg-blue-100 text-blue-800';
    case 'product': return 'bg-green-100 text-green-800';
    case 'professional': return 'bg-purple-100 text-purple-800';
    case 'page': return 'bg-orange-100 text-orange-800';
    default: return 'bg-muted text-muted-foreground';
  }
};

export function SearchDropdown({ 
  results, 
  isSearching, 
  query, 
  onResultClick, 
  onViewAllClick 
}: SearchDropdownProps) {
  if (isSearching) {
    return (
      <Card className="absolute top-full left-0 right-0 mt-2 p-4 shadow-lg z-50">
        <div className="flex items-center justify-center space-x-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm text-muted-foreground">Pesquisando...</span>
        </div>
      </Card>
    );
  }

  if (!query.trim()) {
    return null;
  }

  if (results.length === 0) {
    return (
      <Card className="absolute top-full left-0 right-0 mt-2 p-4 shadow-lg z-50">
        <div className="text-center">
          <Search className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            Nenhum resultado encontrado para "{query}"
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="absolute top-full left-0 right-0 mt-2 shadow-lg z-50 max-h-96 overflow-y-auto">
      <div className="p-2">
        {results.map((result, index) => (
          <Button
            key={result.id}
            variant="ghost"
            className="w-full justify-start h-auto p-3 text-left"
            onClick={() => onResultClick(result)}
          >
            <div className="flex items-start space-x-3 w-full">
              {result.imageUrl && (
                <img 
                  src={result.imageUrl} 
                  alt={result.title}
                  className="w-8 h-8 rounded object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="text-sm font-medium truncate">{result.title}</h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(result.type)}`}>
                    {getTypeLabel(result.type)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {result.description}
                </p>
              </div>
            </div>
          </Button>
        ))}
        
        {results.length >= 8 && (
          <>
            <Separator className="my-2" />
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onViewAllClick}
              className="w-full justify-center"
            >
              <span>Ver todos os resultados</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}